import React from 'react';
import { IHTCalculation, EstateAssets } from '../../types';
import { LedgerRow } from '../ui';

interface TaxSource {
    label: string;
    value: number;
}

function computeTaxSources(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
): TaxSource[] {
    if (!ihtCalc || !assets) return [];

    const b = ihtCalc.breakdown;
    const taxDue = ihtCalc.total_tax_due ?? 0;
    if (taxDue <= 0) return [];

    const taxableValue = b.taxable_value ?? 0;
    if (taxableValue <= 0) return [];

    // Calculate the contribution of each asset class to the gross estate
    // Then proportionally attribute the taxable amount

    const propVal = (assets.properties ?? []).reduce((sum, p) => {
        const val = Number(p.estimated_value) || 0;
        const share = (p.deceased_share_percent ?? 100) / 100;
        return sum + (val * share);
    }, 0);

    const bankVal = (assets.bank_accounts ?? []).reduce((sum, a) =>
        sum + (Number(a.estimated_value) || 0), 0);

    const invVal = (assets.investments ?? []).reduce((sum, i) =>
        sum + (Number(i.estimated_value) || 0), 0);

    const bprVal = (assets.bpr_assets ?? []).reduce((sum, a) =>
        sum + (Number(a.estimated_value) || 0), 0);

    const pensionVal = (assets.pensions ?? []).reduce((sum, p) => {
        if (p.in_estate === false) return sum;
        return sum + (Number(p.estimated_value) || 0);
    }, 0);

    const lifeInsVal = (assets.life_insurance?.exists === true && assets.life_insurance.written_in_trust !== true)
        ? (Number(assets.life_insurance.estimated_value) || 0) : 0;

    const foreignVal = (assets.foreign_assets?.exists === true)
        ? (Number(assets.foreign_assets.value_estimate) || 0) : 0;

    // Total asset value (before deductions)
    const totalAssets = propVal + bankVal + invVal + bprVal + pensionVal + lifeInsVal + foreignVal;
    if (totalAssets <= 0) return [];

    // Proportionally distribute the taxable value across asset categories
    const sources: TaxSource[] = [];

    const addSource = (label: string, assetValue: number) => {
        if (assetValue <= 0) return;
        const proportion = assetValue / totalAssets;
        const contribution = Math.round(taxableValue * proportion);
        if (contribution > 0) {
            sources.push({ label, value: contribution });
        }
    };

    addSource('Property', propVal);
    addSource('Bank accounts', bankVal);
    addSource('Investments', invVal);
    addSource('Business assets', bprVal);
    addSource('Pensions', pensionVal);
    addSource('Life insurance', lifeInsVal);
    addSource('Foreign assets', foreignVal);

    // Sort by contribution descending
    sources.sort((a, b) => b.value - a.value);

    return sources;
}

interface Props {
    ihtCalc?: IHTCalculation | null;
    assets?: EstateAssets;
}

export const TaxSourceBreakdown: React.FC<Props> = ({ ihtCalc, assets }) => {
    const sources = computeTaxSources(ihtCalc, assets);
    if (sources.length === 0) return null;

    const fmt = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
    const totalContribution = sources.reduce((sum, s) => sum + s.value, 0);

    return (
        <div className="bg-white border border-[#E8E4DB] p-5">
            <div className="mb-3">
                <h3 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">
                    Approximate contribution to taxable estate
                </h3>
                <p className="text-[11px] text-[#AFA99F] italic mt-1">
                    After allowances applied proportionally across asset categories.
                </p>
            </div>

            <div>
                {sources.map((source) => (
                    <LedgerRow
                        key={source.label}
                        label={source.label}
                        value={fmt(source.value)}
                    />
                ))}
                <LedgerRow
                    label="Total"
                    value={fmt(totalContribution)}
                    emphasis="total"
                />
            </div>

            <p className="text-[11px] text-[#AFA99F] italic mt-3">
                Shows only categories contributing to the taxable value above the available allowances.
            </p>
        </div>
    );
};
